// Parent view. Reached by a long press on the Base Camp title. Shows the real numbers.

import { h, clear } from '@kit/dom';
import { setTtsEnabled } from '@kit/tts';
import { setSoundEnabled } from '@kit/audio';
import { SKILLS, gradeFor, ringFor, MIN_RUNG, MAX_RUNG } from '../skills';
import { getSave, update, replaceSave, resetSave, skill as skillState, totalStars } from '../state';
import { attemptsSince, exportLog, clearLog, type AttemptRow } from '../log';
import { topBar, toast } from '../ui';
import { go } from '../router';
import type { SkillId } from '../types';

const WEEK = 7 * 24 * 3600 * 1000;
const TEST_STARS = '_test';

export function parentScreen(root: HTMLElement) {
  const bar = topBar('Parent view', { back: 'hub', pills: false });
  const body = h('div.stack', { style: { padding: '0 16px 32px' } });
  root.append(bar, body);

  let week: AttemptRow[] = [];

  async function render() {
    clear(body);
    const s = getSave();
    week = await attemptsSince(WEEK);
    const byRound = week.length;
    const correct = week.filter(a => a.correct).length;

    body.append(
      h('div.card', null,
        h('h3', null, 'This week'),
        h('p', null, `${byRound} answers, ${byRound ? Math.round((correct / byRound) * 100) : 0}% right. ${totalStars(s)} stars total, ${s.gems} gems in hand, ${s.totals.gemsEarned} earned all time.`),
        h('p.muted.small', null, 'She sees ring colors. You see the rung (1 to 10) and the grade it maps to.'),
      ),
    );

    // skills table
    const rows = SKILLS.filter(sk => s.skills[sk.id] || week.some(a => a.skill === sk.id)).map(sk => {
      const st = skillState(sk.id);
      const w = week.filter(a => a.skill === sk.id);
      const wc = w.filter(a => a.correct).length;
      const ring = ringFor(st.rung);
      const pin = s.pins[sk.id];
      return h('tr', null,
        h('td', null, h('strong', null, sk.label), h('br'), h('span.muted.small', null, `"${sk.kid}"`)),
        h('td', null, h('span.ring', { style: { color: ring.color, background: ring.color } }), ` ${st.rung}`, h('br'), h('span.muted.small', null, gradeFor(st.rung))),
        h('td', null, w.length ? `${wc}/${w.length}` : '-'),
        h('td', null, pinControl(sk.id, pin)),
      );
    });
    body.append(h('div.card', null,
      h('h3', null, 'Skills'),
      rows.length ? h('table.parent', null, h('thead', null, h('tr', null, h('th', null, 'Skill'), h('th', null, 'Rung'), h('th', null, '7 days'), h('th', null, 'Pin'))), h('tbody', null, rows)) : h('p.muted', null, 'No skills touched yet.'),
      h('p.muted.small', { style: { marginTop: '8px' } }, 'Pin clamps a skill between two rungs. Leave both blank for the normal rule: up after 3 clean rights in a row, down after 2 wrongs in a row.'),
    ));

    // settings
    const sign = h('input', { type: 'text', value: s.settings.sign, maxLength: 4 }) as HTMLInputElement;
    sign.addEventListener('change', () => update(x => { x.settings.sign = sign.value.toUpperCase().slice(0, 4) || 'AR'; }));
    body.append(h('div.card.stack', null,
      h('h3', null, 'Settings'),
      toggle('Read words aloud', s.settings.tts, v => { update(x => { x.settings.tts = v; }); setTtsEnabled(v); }),
      toggle('Sounds', s.settings.sound, v => { update(x => { x.settings.sound = v; }); setSoundEnabled(v); }),
      h('label', null, 'Letters on the house', sign),
      h('div', null, 'Grant gems (testing)', h('div.row.wrap', null,
        h('button.btn.small', { onclick: () => update(x => { x.gems += 100; }) }, '+100'),
        h('button.btn.small', { onclick: () => update(x => { x.gems += 500; }) }, '+500'),
        h('button.btn.small', { onclick: () => { update(x => { x.gems += 1000; }); toast('+1000 gems'); render(); } }, '+1000'),
      )),
      // test stars live in a pseudo-game so they count toward unlocks but never show on a game card
      h('div', null, `Grant stars (testing)${(s.stars[TEST_STARS]?.['1'] ?? 0) ? `: ${s.stars[TEST_STARS]['1']} test stars now` : ''}`, h('div.row.wrap', null,
        h('button.btn.small', { onclick: () => { update(x => { const g = (x.stars[TEST_STARS] ||= {}); g['1'] = (g['1'] || 0) + 1000; }); toast('+1000 stars'); render(); } }, '+1000'),
        h('button.btn.small.ghost', { onclick: () => { update(x => { delete x.stars[TEST_STARS]; }); toast('Test stars removed'); render(); } }, 'Remove test stars'),
      )),
    ));

    // data
    body.append(h('div.card.stack', null,
      h('h3', null, 'Save data'),
      h('p.muted.small', null, 'Everything lives on this phone. Export now and then; import on a new phone.'),
      h('div.grid2', null,
        h('button.btn', { onclick: doExport }, 'Export'),
        h('button.btn', { onclick: doImport }, 'Import'),
      ),
      h('button.btn.ghost.small', { onclick: doReset, style: { color: 'var(--bad)' } }, 'Start completely over'),
    ));
  }

  function pinControl(id: SkillId, pin?: [number, number]): HTMLElement {
    const opts = (v: number | undefined) => ['', ...Array.from({ length: MAX_RUNG - MIN_RUNG + 1 }, (_, i) => String(i + MIN_RUNG))].map(o => h('option', { value: o, selected: (v === undefined ? '' : String(v)) === o }, o || '-'));
    const lo = h('select', null, opts(pin?.[0])) as HTMLSelectElement;
    const hi = h('select', null, opts(pin?.[1])) as HTMLSelectElement;
    const apply = () => update(x => {
      const a = lo.value ? Number(lo.value) : MIN_RUNG, b = hi.value ? Number(hi.value) : MAX_RUNG;
      if (!lo.value && !hi.value) delete x.pins[id];
      else x.pins[id] = [Math.min(a, b), Math.max(a, b)];
      const st = skillState(id);
      if (x.pins[id]) st.rung = Math.max(x.pins[id]![0], Math.min(x.pins[id]![1], st.rung));
    });
    lo.addEventListener('change', apply); hi.addEventListener('change', apply);
    return h('div.row', { style: { gap: '4px' } }, lo, h('span.muted', null, 'to'), hi);
  }

  function toggle(label: string, on: boolean, set: (v: boolean) => void): HTMLElement {
    const cb = h('input', { type: 'checkbox', checked: on, style: { width: '24px', height: '24px' } }) as HTMLInputElement;
    cb.addEventListener('change', () => set(cb.checked));
    return h('label.row', null, cb, label);
  }

  async function doExport() {
    const data = { save: getSave(), log: await exportLog(), exportedAt: new Date().toISOString() };
    const blob = new Blob([JSON.stringify(data)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `basecamp-save-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    toast('Exported.');
  }

  function doImport() {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = 'application/json';
    input.onchange = async () => {
      const f = input.files?.[0]; if (!f) return;
      try {
        const data = JSON.parse(await f.text());
        if (!data.save || data.save.v !== 1) throw new Error('bad');
        replaceSave(data.save);
        toast('Imported. The attempt log is not restored, only the save.');
        render();
      } catch { toast('That file did not look like a save.'); }
    };
    input.click();
  }

  function doReset() {
    const word = prompt('Type RESET to erase everything on this phone.');
    if (word !== 'RESET') return;
    resetSave();
    clearLog();
    toast('Fresh start.');
    go('studio');
  }

  render();
}
